import React, { useMemo, useState } from "react";
import { ComingSoonTooltip } from "@components/widgets";
import { Button, Space, Select, Tag } from "antd";
import { BsBoxArrowUpRight, BsChevronDown } from "react-icons/bs";
import RevstackTabsSection from "@pages/revstack-dashboards/RevstackTabsSection";
import RevstackDatePicker from "@components/drop-downs/revstack-date-picker/RevstackDatePicker";
import RevstackChannelsSelect from "@components/drop-downs/RevstackChannelsSelect";
import { CA_DD_MODULES, RevstackFilterTypes, OptionValues, RevstackDashboardSubModules } from "@ts/common-types";
import { buildWidgetCardFilters } from "@ts/revstack/ui-utils";
import { useSelector } from "react-redux";
import { titleCase } from "@/ts/utils";

interface RevstackDrilldownDrawerHeader {
    title: string;
    primaryFilterSubLabel: string;
    titleIcon: React.ReactNode;
    primaryFilterOptions: { label: string; value: number | string }[];
    onPrimaryFilterChange: (value: number | string) => void;
    primaryFilterValue: number | string;
    items: any;
    activeTab: CA_DD_MODULES;
    setActiveTab: (activeKey: CA_DD_MODULES) => void;
    selectedPrimaryFilterLabel: string;
    isTrendsViewRequired: boolean;
    isAdvancedChartsRequired: boolean;
    onTrendsViewClick: () => void;
    onAdvancedChartsClick: () => void;
    titlePills: string[];
    showCategorySelect?: boolean;
    showRegionSelect?: boolean;
    config: any;
    secondaryFilterValue: any;
    secondaryFilterOptions: any;
    onSecondaryFilterChange: (value: any, filterIndex?: number) => void;
    selectedSecondaryLabel: string;
}

const RevstackDrilldownDrawerHeader: React.FC<
    Partial<RevstackDrilldownDrawerHeader>
> = ({
    title,
    primaryFilterSubLabel,
    titleIcon,
    isAdvancedChartsRequired = false,
    config = {},
    primaryFilterOptions = [],
    onPrimaryFilterChange,
    primaryFilterValue,
    secondaryFilterValue,
    items = [],
    activeTab,
    setActiveTab,
    selectedPrimaryFilterLabel,
    onTrendsViewClick,
    onAdvancedChartsClick,
    titlePills = [],
    secondaryFilterOptions = [],
    onSecondaryFilterChange,
    selectedSecondaryLabel,
}) => {
    // Local state to track pending selections for multiSelect filters
    const [pendingSelections, setPendingSelections] = useState<Record<string, any[]>>({});
    const headersConfig = config?.header || {};
    const tabsConfig = config?.tabs || {};
    const hideTabsSection = tabsConfig?.hideTabsSection || false;
    const { mainComponents = [], filtersComponents = [] } =
        headersConfig?.components?.reduce(
            (acc: any, component: any) => {
                if (component.section === "main") {
                    acc.mainComponents = component?.elements || [];
                } else if (component.section === "filters") {
                    acc.filtersComponents = component?.elements || [];
                }
                return acc;
            },
            { mainComponents: [], filtersComponents: [] }
        ) || {};
    const revstackChannel = useSelector((state: any) => state.revstackChannel.value);
    const isMultiChannelModule = useMemo(() => (revstackChannel.length > 1), [revstackChannel]);
    const updatedFiltersComponents = useMemo(() => {
        if (!filtersComponents.length) return [];

        // Handle multiple secondary filters case
        if (filtersComponents.length > 1) {
            return filtersComponents.map((component: any, index: number) => {
                const filterKey = component?.filterKey || `filter_${index}`;
                const currentValue = Array.isArray(secondaryFilterValue)
                    ? secondaryFilterValue[index] || []
                    : [];
                const pendingValue = pendingSelections[filterKey] || currentValue;

                return {
                    ...component,
                    props: {
                        ...component?.props,
                        options: secondaryFilterOptions[index] || [],
                        value: pendingValue,
                        onChange: (value: any) => {
                            if(component?.type === RevstackFilterTypes.MULTI_SELECT){
                                // For multiSelect, store the pending selection locally
                                setPendingSelections(prev => ({
                                    ...prev,
                                    [filterKey]: value
                                }));
                            } else {
                                // For non-multiSelect, apply immediately
                                onSecondaryFilterChange?.(value, index);
                            }
                        },
                        onOpenChange: (open: boolean) => {
                            if(component?.type === RevstackFilterTypes.MULTI_SELECT && !open) {
                                // Apply pending selection when dropdown closes
                                const finalValue = pendingSelections[filterKey] || currentValue;
                                onSecondaryFilterChange?.(finalValue, index);
                                // Clear pending selection
                                setPendingSelections(prev => {
                                    const newPending = { ...prev };
                                    delete newPending[filterKey];
                                    return newPending;
                                });
                            }
                            component?.props?.onOpenChange?.(open);
                        },
                    },
                };
            });
        }

        // Handle single secondary filter case
        if (filtersComponents.length === 1) {
            const component = filtersComponents[0];
            const filterKey = component?.filterKey || 'single_filter';
            const currentValue = secondaryFilterValue?.[0] || [];
            const pendingValue = pendingSelections[filterKey] || currentValue;

            return [{
                ...component,
                props: {
                    ...component?.props,
                    options: secondaryFilterOptions || [],
                    styles: {
                        height: "35px"
                    },
                    value: pendingValue,
                    onChange: (val: any) => {
                        if(component?.type === RevstackFilterTypes.MULTI_SELECT){
                            // For multiSelect, store the pending selection locally
                            setPendingSelections(prev => ({
                                ...prev,
                                [filterKey]: val
                            }));
                        } else {
                            // For non-multiSelect, apply immediately
                            onSecondaryFilterChange?.(val);
                        }
                    },
                    onOpenChange: (open: boolean) => {
                        if(component?.type === RevstackFilterTypes.MULTI_SELECT && !open) {
                            // Apply pending selection when dropdown closes
                            const finalValue = pendingSelections[filterKey] || currentValue;
                            onSecondaryFilterChange?.(finalValue);
                            // Clear pending selection
                            setPendingSelections(prev => {
                                const newPending = { ...prev };
                                delete newPending[filterKey];
                                return newPending;
                            });
                        }
                        component?.props?.onOpenChange?.(open);
                    },
                },
            }];
        }

        return [];
    }, [filtersComponents, secondaryFilterOptions, secondaryFilterValue, onSecondaryFilterChange, pendingSelections]);

    return (
        <div
            id="revstack-drilldown-header"
            className="w-100 d-flex flex-column align-items-center"
        >
            <div
                className="d-flex justify-content-between align-items-center gap-4 w-100"
                style={{
                    padding: "15px 60px 15px 20px",
                    borderBottom: "1px solid #CBD9E7",
                }}
            >
                <div id="main-section" className="d-flex align-items-center">
                    <i className="icon-btn">{titleIcon}</i>
                    {mainComponents.map((component: any) =>
                        component.type === RevstackFilterTypes.SELECT ? (
                            <Select
                                className="me-3"
                                placeholder={
                                    component?.props?.placeholder?.length > 0
                                        ? component?.props?.placeholder
                                        : "Select Filters"
                                }
                                value={primaryFilterValue}
                                onChange={onPrimaryFilterChange}
                                style={{
                                    minWidth: 250,
                                    maxWidth: 250,
                                }}
                                labelRender={() => (
                                    <div className="d-flex flex-column align-items-start justify-content-center">
                                        <h4 className={`mb-0 ${primaryFilterSubLabel?.length > 0 ? "mt-1" : ""}`}>
                                            {selectedPrimaryFilterLabel}
                                        </h4>
                                        {primaryFilterSubLabel?.length > 0 && (
                                            <span
                                                className="sub-title text-secondary fs-9 fw-600"
                                                style={{
                                                    marginTop: "-8px",
                                                }}
                                            >
                                                {titleCase(primaryFilterSubLabel)}
                                            </span>
                                        )}
                                    </div>
                                )}
                                suffixIcon={
                                    <BsChevronDown
                                        style={{
                                            color: "#615E83",
                                            width: 15,
                                            height: 20,
                                            strokeWidth: 1,
                                        }}
                                    />
                                }
                                variant="borderless"
                                options={primaryFilterOptions}
                                showSearch={false}
                            />
                        ) : component.type === "text" ? (
                            <div
                                className="d-flex align-items-center gap-3"
                                key={component.id}
                            >
                                <h4 className="mb-0 ms-2">
                                    {titleCase(title)}
                                </h4>
                                {titlePills?.map((pill, index) => (
                                    <Tag
                                        key={index}
                                        bordered={false}
                                        style={{
                                            background: "#e5f8ff",
                                            color: "#1e1b39",
                                            borderRadius: 6,
                                            fontWeight: 600,
                                            fontSize: 12,
                                            padding: "0 10px",
                                            display: "inline-flex",
                                            alignItems: "center",
                                            height: 26,
                                            width: "fit-content",
                                            textTransform: "uppercase",
                                        }}
                                    >
                                        {pill}
                                    </Tag>
                                ))}
                            </div>
                        ) : component.placement === "suffix" &&
                          component.type === "detailed-button" ? (
                            <ComingSoonTooltip key="detailed-view-btn-tooltip">
                                <Button
                                    className="detailed-view-btn mb-1 ms-3"
                                    variant="filled"
                                    size="small"
                                    icon={<BsBoxArrowUpRight />}
                                    iconPosition="end"
                                    style={{ fontSize: "11px" }}
                                    disabled={true}
                                >
                                    Detailed View
                                </Button>
                            </ComingSoonTooltip>
                        ) : null
                    )}
                </div>
                <Space align="center" direction="horizontal" size={12}>
                    {filtersComponents.length > 0 && (
                        <div id="filters-section">
                            {buildWidgetCardFilters({
                                filters: updatedFiltersComponents || [],
                            })}
                        </div>
                    )}
                    <RevstackDatePicker editable={false} />
                    {!isMultiChannelModule ? (
                        <RevstackChannelsSelect editable={false} />
                    ) : null}
                </Space>
            </div>
            <div className="w-100" style={{ padding: "0px 24px" }}>
                {!hideTabsSection && (
                    <RevstackTabsSection
                        {...{
                            items,
                            activeTab,
                            isTrendsViewRequired:
                                tabsConfig?.isTrendsViewRequired || false,
                            isAdvancedChartsRequired,
                            onTrendsViewClick,
                            onAdvancedChartsClick,
                        }}
                        type="drilldown"
                        setActiveTab={(activeKey) =>
                            setActiveTab(activeKey as CA_DD_MODULES)
                        }
                    />
                )}
            </div>
        </div>
    );
};

export default RevstackDrilldownDrawerHeader;